import { STATUS_WARN } from 'styles/tokens';

// Tag coloring shared by the rendered Markdown preview and the CodeMirror editor so
// both surfaces highlight prompt tags identically.
//
//   - Conditional / control-flow tags (IF / IF_LEAD / *_HOURS, open & close) are
//     colored by nesting depth, cycling this palette so nested blocks stay legible.
//   - Non-conditional value tags (<CLIENT:…>, <SECTION:…>, <CURRENT_DATE>, …) are
//     orange — they insert a value rather than gating a block.
//   - Invalid (unmatched / orphan) tags are red.
export const PROMPT_CONDITIONAL_TAG_COLORS = ['#7F3C8D', '#0072B2', '#56B4E9', '#9FC6EA'];
export const PROMPT_VALUE_TAG_COLOR = STATUS_WARN;
export const PROMPT_INVALID_TAG_COLOR = '#D32F2F';

// Names of tags that open a conditional block and therefore require a closing tag.
const isConditionalName = (name: string): boolean =>
  name.startsWith('IF:') || name.startsWith('IF_LEAD:') || name === 'WORKING_HOURS' || name === 'OUT_OF_WORKING_HOURS';

export const requiresClosingTag = isConditionalName;

/** True for an opening or closing conditional tag (`<IF:attr>`, `</WORKING_HOURS>`, …). */
export const isConditionalTag = (tagText: string): boolean => {
  const name = tagText.replace(/^<\/?/, '').replace(/>$/, '');
  return isConditionalName(name);
};

/**
 * For each `<TAG>` in source order, return its nesting depth and whether it is
 * invalid (an orphan closer, or a block opener that never closes).
 */
export const analyzePromptTags = (raw: string): { depths: number[]; invalid: boolean[] } => {
  const tags = Array.from(raw.matchAll(/<[^<>]+>/g)).map((m) => m[0]);
  const isBlock = new Array(tags.length).fill(false);
  const invalid = new Array(tags.length).fill(false);
  const pairStack: Array<{ name: string; idx: number }> = [];
  for (let i = 0; i < tags.length; i++) {
    const tag = tags[i];
    if (tag.startsWith('</')) {
      const name = tag.slice(2, -1);
      let matched = false;
      for (let j = pairStack.length - 1; j >= 0; j--) {
        if (pairStack[j].name === name) {
          isBlock[pairStack[j].idx] = true;
          pairStack.splice(j, 1);
          matched = true;
          break;
        }
      }
      // Closing tag with no prior matching opener.
      if (!matched) invalid[i] = true;
    } else {
      pairStack.push({ name: tag.slice(1, -1), idx: i });
    }
  }
  // Block-style openers (IF:…, WORKING_HOURS…) that never got a matching close.
  for (const { name, idx } of pairStack) {
    if (requiresClosingTag(name)) invalid[idx] = true;
  }
  const depths: number[] = [];
  let depth = 0;
  for (let i = 0; i < tags.length; i++) {
    const tag = tags[i];
    if (tag.startsWith('</')) {
      depth = Math.max(0, depth - 1);
      depths.push(depth);
    } else {
      depths.push(depth);
      if (isBlock[i]) depth++;
    }
  }
  return { depths, invalid };
};

/** Resolve the highlight color for a single tag given its depth and validity. */
export const promptTagColor = (tagText: string, depth: number, invalid: boolean): string => {
  if (invalid) return PROMPT_INVALID_TAG_COLOR;
  if (isConditionalTag(tagText)) {
    return PROMPT_CONDITIONAL_TAG_COLORS[depth % PROMPT_CONDITIONAL_TAG_COLORS.length];
  }
  return PROMPT_VALUE_TAG_COLOR;
};
